import { useEffect, useMemo, useRef, useState } from 'react'

import { getSession, signOut, useSession } from '@/shared/services/session/compat'

import { useRenderContext } from '@/shared/contexts/RenderContext'
import { useAppRouter } from '@/shared/hooks/useAppRouter'
import { NativeBridgeEvents, sendToNative } from '@/shared/lib/native-bridge'
import { authPaths } from '@/shared/lib/routing/auth'
import { getAccessToken } from '@/shared/services/session/instance'

import {
  AuthBootstrapShell,
  completePostAuthNavigation,
  isNoTicketAuthError,
  resolveAuthSignInContext,
  shouldShowAuthBootstrapShell,
  shouldShowNoTicketView,
  shouldStripStaleNoTicketError
} from '../pages/shared'

type SessionWithToken = {
  user?: unknown
  accessToken?: string
  error?: string
}

/**
 * Exigia `accessToken` e ausência de `error` porque o Auth.js podia reportar
 * `authenticated` antes de o token aterrissar, ou com um token que o
 * `RefreshAccessTokenError` já marcara como inutilizável.
 *
 * Nenhuma das duas condições existe agora: o `adopt()` do `@evnttz/session`
 * grava o token **antes** de mudar o status, então `authenticated` já implica
 * token vivo; e uma renovação recusada limpa o estado em vez de deixar a sessão
 * de pé com token morto. A garantia continua a mesma, com uma condição só.
 */
function hasUsableAuthSession(
  sessionStatus: string,
  session: SessionWithToken | null | undefined
) {
  return sessionStatus === 'authenticated' && !!session?.user
}

type UsePostAuthOrchestrationOptions = {
  digitalProductId?: string
}

export function usePostAuthOrchestration({
  digitalProductId: digitalProductIdProp
}: UsePostAuthOrchestrationOptions = {}) {
  const router = useAppRouter()
  const { inNativeAppWebView } = useRenderContext()
  const { status: sessionStatus, data: session } = useSession()
  const sessionWithToken = session as SessionWithToken | null | undefined
  const reauthHandledRef = useRef(false)
  const nativeSignInRequestedRef = useRef(false)
  const ticketCheckDoneRef = useRef(false)
  const staleSessionHandledRef = useRef(false)
  const switchingAccountRef = useRef(false)
  const [sessionTimedOut, setSessionTimedOut] = useState(false)
  const [checkingTicket, setCheckingTicket] = useState(false)

  const usableSession = hasUsableAuthSession(sessionStatus, sessionWithToken)

  const authContext = useMemo(
    () =>
      router.isReady
        ? resolveAuthSignInContext(router, digitalProductIdProp)
        : null,
    [digitalProductIdProp, router, router.isReady, router.asPath, router.query]
  )

  const isNoTicketError = isNoTicketAuthError(router.query?.error)
  const showNoTicketView = shouldShowNoTicketView({
    isNoTicketError,
    usableSession
  })

  useEffect(() => {
    if (sessionStatus !== 'loading') {
      setSessionTimedOut(false)
      return undefined
    }

    const timeout = setTimeout(() => setSessionTimedOut(true), 8000)
    return () => clearTimeout(timeout)
  }, [sessionStatus])

  useEffect(() => {
    if (!usableSession) {
      switchingAccountRef.current = false
    }
  }, [usableSession])

  useEffect(() => {
    if (sessionStatus !== 'authenticated' || usableSession) {
      staleSessionHandledRef.current = false
      return
    }

    if (staleSessionHandledRef.current) return
    staleSessionHandledRef.current = true
    ticketCheckDoneRef.current = false
    setCheckingTicket(false)

    void signOut({ redirect: false })
  }, [sessionStatus, usableSession])

  useEffect(() => {
    if (!router.isReady || !authContext) return
    if (
      !shouldStripStaleNoTicketError({
        isNoTicketError,
        usableSession,
        sessionStatus,
        sessionTimedOut
      })
    ) {
      return
    }

    router.replace(
      authPaths.signIn({
        callbackUrl: authContext.returnTo
      })
    )
  }, [
    authContext,
    isNoTicketError,
    router,
    sessionStatus,
    sessionTimedOut,
    usableSession
  ])

  useEffect(() => {
    if (!router.isReady || !authContext) return
    if (switchingAccountRef.current) return
    if (isNoTicketError) {
      setCheckingTicket(false)
      return
    }
    if (sessionStatus === 'loading' && !sessionTimedOut) return

    const reauthParam = router.query?.reauth
    const reauthValue = Array.isArray(reauthParam) ? reauthParam[0] : reauthParam
    const forceReauth = reauthValue === '1' || reauthValue === 'true'

    if (forceReauth && sessionStatus === 'authenticated') {
      if (reauthHandledRef.current) return
      reauthHandledRef.current = true
      ticketCheckDoneRef.current = false

      void signOut({ redirect: false }).finally(() => {
        const url = new URL(window.location.href)
        url.searchParams.delete('reauth')
        window.history.replaceState({}, '', `${url.pathname}${url.search}`)
      })
      return
    }

    if (!usableSession) return
    if (ticketCheckDoneRef.current) return
    ticketCheckDoneRef.current = true

    setCheckingTicket(true)
    completePostAuthNavigation({
      router,
      returnTo: authContext.returnTo,
    // O token não vem mais do objeto de sessão: ele vive em memória e não chega
    // à UI. `getAccessToken()` é a leitura legítima aqui — a reconciliação de
    // acesso é a camada de auth, não tela.
      accessToken: getAccessToken() ?? undefined,
      eventSlug: authContext.eventSlug
    })
      .then(result => {
        // Entrada pós-login: se a reconciliação não concluiu, segue o destino
        // e deixa Guard/recovery tentar de novo — distinto do retry na tela
        // NoTicket, que permanece no lugar.
        if (result === 'unknown') {
          ticketCheckDoneRef.current = false
          window.location.replace(authContext.returnTo)
        }
      })
      .catch(() => {
        ticketCheckDoneRef.current = false
        window.location.replace(authContext.returnTo)
      })
      .finally(() => {
        setCheckingTicket(false)
      })
  }, [
    authContext,
    isNoTicketError,
    router.isReady,
    router.query?.reauth,
    sessionStatus,
    sessionTimedOut,
    usableSession
  ])

  useEffect(() => {
    if (!inNativeAppWebView || !authContext) {
      nativeSignInRequestedRef.current = false
      return
    }

    if (sessionStatus === 'loading' && !sessionTimedOut) return
    if (usableSession) return
    if (nativeSignInRequestedRef.current) return

    nativeSignInRequestedRef.current = true
    sendToNative({
      type: NativeBridgeEvents.AUTH_REQUIRED,
      callbackUrl: authContext.normalizedCallback
    })
  }, [authContext, inNativeAppWebView, sessionStatus, sessionTimedOut, usableSession])

  const showShell = shouldShowAuthBootstrapShell({
    isNoTicketError: showNoTicketView,
    sessionStatus,
    sessionTimedOut,
    usableSession,
    checkingTicket,
    hasAuthContext: !!authContext,
    inNativeAppWebView
  })

  const handleSignInSuccess = async () => {
    if (!authContext) return

    const nextSession = (await getSession()) as SessionWithToken | null
    if (!hasUsableAuthSession('authenticated', nextSession)) return

    setCheckingTicket(true)
    ticketCheckDoneRef.current = true

    try {
      const result = await completePostAuthNavigation({
        router,
        returnTo: authContext.returnTo,
        accessToken: getAccessToken() ?? undefined,
        eventSlug: authContext.eventSlug
      })
      if (result === 'unknown') {
        ticketCheckDoneRef.current = false
        window.location.replace(authContext.returnTo)
      }
    } catch {
      ticketCheckDoneRef.current = false
      window.location.replace(authContext.returnTo)
    } finally {
      setCheckingTicket(false)
    }
  }

  const retryAccessCheck = async () => {
    if (!authContext) return

    const nextSession = (await getSession()) as SessionWithToken | null
    if (!hasUsableAuthSession('authenticated', nextSession)) {
      ticketCheckDoneRef.current = false
      router.replace(
        authPaths.signIn({
          callbackUrl: authContext.returnTo
        })
      )
      return
    }

    ticketCheckDoneRef.current = false
    setCheckingTicket(true)

    try {
      // Hits GET user_access → ingresso existente ou default entry.
      await completePostAuthNavigation({
        router,
        returnTo: authContext.returnTo,
        accessToken: getAccessToken() ?? undefined,
        eventSlug: authContext.eventSlug
      })
    } catch {
      // Mantém o participante na tela NoTicket — sem bounce cego.
    } finally {
      setCheckingTicket(false)
    }
  }

  const useAnotherAccount = async () => {
    if (!authContext) return

    switchingAccountRef.current = true
    ticketCheckDoneRef.current = false
    reauthHandledRef.current = false
    setCheckingTicket(false)

    await signOut({ redirect: false })

    router.replace(
      authPaths.signIn({
        callbackUrl: authContext.returnTo
      })
    )
  }

  const leaveSignIn = () => {
    switchingAccountRef.current = true
    router.push('/sign-out')
  }

  return {
    authContext,
    showShell,
    isNoTicketError: showNoTicketView,
    usableSession,
    checkingTicket,
    handleSignInSuccess,
    retryAccessCheck,
    useAnotherAccount,
    leaveSignIn,
    AuthBootstrapShell
  }
}
